import { CalendarOutlined } from '@ant-design/icons';
import { Checkbox, DatePicker, Input, Radio, Select, Switch } from 'antd';

import { ContractFileUpload } from '@/components/form/shared/ContractFileUpload';
import CurrencySelect from '@/components/form/shared/currencies/CurrencySelect';
import GooglePlacesInput from '@/components/form/shared/GooglePlacesInput';
import ImageUploadField from '@/components/form/shared/ImageUploadField';
import { InputNumberInput } from '@/components/form/shared/InputNumberInput';
import {
  MultiFileUpload,
  type MultiFileUploadDraftProps,
} from '@/components/form/shared/MultiFileUpload';
import NullableBooleanSwitch from '@/components/form/shared/NullableBooleanSwitch';
import ScheduleDateTimePicker from '@/components/form/shared/ScheduleDateTimePicker';
import i18n from '@/i18n';
import { formatShortDate } from '@/utils/formatDate';
import { defaultShowSearchForSelectMode } from '@/utils/selectHelpers';

import type { FieldConfig, FieldRenderContext } from './types';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import type { RadioGroupProps } from 'antd/es/radio';
import type { SelectProps } from 'antd/es/select';
import type { RcFile } from 'antd/lib/upload';
import type { CSSProperties, ReactNode } from 'react';

function resolveControlSize(
  componentProps: Record<string, unknown>,
  inputSize: SizeType,
): SizeType {
  return (componentProps.size as SizeType | undefined) ?? inputSize;
}

/** Renders a field control for all types except `custom` and `list`. */
export function renderBasicFieldControl(
  field: FieldConfig,
  ctx: FieldRenderContext,
): ReactNode {
  const { inputSize, form } = ctx;
  const componentProps = field.componentProps ?? {};
  const resolvedSize = resolveControlSize(componentProps, inputSize);

  switch (field.type) {
    case 'text':
      return (
        <Input
          {...componentProps}
          size={resolvedSize}
          placeholder={field.placeholder}
        />
      );
    case 'number':
      return (
        <InputNumberInput
          {...componentProps}
          size={resolvedSize}
          style={{
            width: '100%',
            ...(componentProps.style as CSSProperties | undefined),
          }}
          placeholder={field.placeholder}
        />
      );
    case 'textarea':
      return (
        <Input.TextArea
          autoSize={{ minRows: 3 }}
          placeholder={field.placeholder}
          {...componentProps}
        />
      );
    case 'password':
      return (
        <Input.Password
          {...componentProps}
          size={resolvedSize}
          placeholder={field.placeholder}
        />
      );
    case 'select': {
      const selectProps = componentProps as SelectProps;
      const { showSearch, mode, ...restSelectProps } = selectProps;
      return (
        <Select
          {...restSelectProps}
          size={resolvedSize}
          mode={mode}
          showSearch={defaultShowSearchForSelectMode(mode, showSearch)}
          placeholder={field.placeholder ?? selectProps.placeholder}
          options={field.options ?? selectProps.options}
        />
      );
    }
    case 'currency':
      return (
        <CurrencySelect
          {...componentProps}
          size={resolvedSize}
          placeholder={field.placeholder}
        />
      );
    case 'switch':
      return <Switch {...componentProps} />;
    case 'nullableBoolean':
      return <NullableBooleanSwitch {...componentProps} />;
    case 'radio':
      return (
        <Radio.Group
          options={field.options as RadioGroupProps['options']}
          {...componentProps}
        />
      );
    case 'tabRadio': {
      const tabRadioProps = componentProps as {
        data?: { title: string | number; value: string | number }[];
        customClass?: string;
      };
      const {
        data = [],
        customClass,
        ...radioGroupProps
      } = {
        ...componentProps,
        ...tabRadioProps,
      };
      return (
        <Radio.Group className={customClass} {...radioGroupProps}>
          {data.map((el) => (
            <Radio key={el.value} value={el.value}>
              {i18n.t(String(el.title))}
            </Radio>
          ))}
        </Radio.Group>
      );
    }
    case 'places':
      return (
        <GooglePlacesInput
          {...componentProps}
          size={resolvedSize}
          placeholder={field.placeholder}
        />
      );
    case 'date': {
      const { style: dateStyle, ...dateProps } = componentProps;
      return (
        <DatePicker
          suffixIcon={<CalendarOutlined />}
          format={(value) => formatShortDate(value)}
          {...dateProps}
          size={resolvedSize}
          placeholder={field.placeholder ?? ''}
          style={{
            width: '100%',
            ...(dateStyle as CSSProperties | undefined),
          }}
        />
      );
    }
    case 'datetime':
      return (
        <ScheduleDateTimePicker
          {...componentProps}
          size={resolvedSize}
          placeholder={field.placeholder}
        />
      );
    case 'checkbox':
      return <Checkbox {...componentProps}>{field.label}</Checkbox>;
    case 'image':
      return <ImageUploadField {...componentProps} />;
    case 'hidden':
      return <Input type="hidden" {...componentProps} />;
    case 'file': {
      const fileProps = componentProps as {
        file?: string | FormData | undefined;
        setFile?: (file: string | FormData | undefined) => void;
        fieldName?: string;
        existingContractUrl?: string;
        showExistingFile?: boolean;
        onFileChange?: () => void;
        deleteButtonDanger?: boolean;
        beforeUpload?: (file: RcFile) => boolean;
        accept?: string;
        downloadLabel?: string;
        chooseFileLabel?: string;
        dataTestId?: string;
      };
      return (
        <ContractFileUpload
          form={form}
          file={fileProps.file}
          setFile={fileProps.setFile ?? (() => undefined)}
          fieldName={fileProps.fieldName}
          label={typeof field.label === 'string' ? field.label : undefined}
          existingContractUrl={fileProps.existingContractUrl}
          showExistingFile={fileProps.showExistingFile}
          onFileChange={fileProps.onFileChange}
          deleteButtonDanger={fileProps.deleteButtonDanger}
          beforeUpload={fileProps.beforeUpload}
          accept={fileProps.accept}
          downloadLabel={fileProps.downloadLabel}
          chooseFileLabel={fileProps.chooseFileLabel}
          help={typeof field.help === 'string' ? field.help : undefined}
          dataTestId={fileProps.dataTestId}
        />
      );
    }
    case 'files': {
      const filesProps =
        componentProps as Partial<MultiFileUploadDraftProps> & {
          accept?: string;
          maxCount?: number;
          chooseFileLabel?: string;
          dataTestId?: string;
        };
      return (
        <MultiFileUpload
          label={typeof field.label === 'string' ? field.label : undefined}
          help={typeof field.help === 'string' ? field.help : undefined}
          accept={filesProps.accept}
          maxCount={filesProps.maxCount}
          persistedFiles={filesProps.persistedFiles}
          pendingRemovalIds={filesProps.pendingRemovalIds}
          stagedFiles={filesProps.stagedFiles}
          onRemovePersisted={filesProps.onRemovePersisted}
          onStagedFilesChange={filesProps.onStagedFilesChange}
          beforeUpload={filesProps.beforeUpload}
          chooseFileLabel={filesProps.chooseFileLabel}
          dataTestId={filesProps.dataTestId}
        />
      );
    }
    default:
      return (
        <Input
          {...componentProps}
          size={resolvedSize}
          placeholder={field.placeholder}
        />
      );
  }
}
